import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useContent } from '@/stores/RootStore';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import SubPageHero from '@/components/SubPageHero';
import OrgChart from '@/components/OrgChart';
import Timeline from '@/components/Timeline';
import TalentValues from '@/components/TalentValues';
import Button from '@/components/ui/Button';
import c from './Company.module.scss';
import x from './CompanyCertifications.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';
const Ic = ({ n }: { n: string }) => <div className={x.cardIcon}>{GUIDE_ICON[n]}</div>; // [2026-10-08] 라이트 카드 라인아이콘

// ============================================================================
//  회사소개 분화 — 독립 URL 페이지 (기획서 v7 8)
//  [2026-10-08] 앵커(/company#id) → 독립 URL. 기존 /company 섹션 콘텐츠·컴포넌트 재사용(보존).
//    /company/history(연혁)·/organization(조직도)·/careers(인재상·채용)·/rd(연구개발·신규)·/location(오시는 길)
//    연구개발(rd)은 신규 → 준비중 위주. 채용공고·지원방법은 준비중.
//  ★NAV 메뉴 연결은 B안(부장 확정 후). 현재는 직접 URL + 기존 /company 앵커 유지.
// ============================================================================

const HERO = '/images/company/subpage-hero.webp';
const crumbs = (last: string) => [{ label: 'HOME', to: '/' }, { label: '회사소개', to: '/company' }, { label: last }];
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

// ── 연혁 ──
export const CompanyHistory = () => (
  <main>
    <PageLoader />
    <SubPageHero title="주요 연혁" subtitle="기술·사업 기반과 EVS 법인 연혁" image={HERO} align="left" crumbs={crumbs('연혁')} />
    <section className={c.section}>
      <div ref={useReveal<HTMLDivElement>()}>
        <div className={c.inner}><p className={`${c.eyebrow} anim-scroll`}>HISTORY</p><h2 className={`${c.headline} anim-scroll`}>주요 연혁</h2></div>
        <div className={`${c.timelineWrap} anim-scroll`}><Timeline /></div>
      </div>
    </section>
  </main>
);

// ── 조직도 ──
export const CompanyOrganization = () => (
  <main>
    <PageLoader />
    <SubPageHero title="조직도" subtitle="경영·영업·기술·연구와 고객지원 흐름" image={HERO} align="left" crumbs={crumbs('조직도')} />
    <section className={c.section}>
      <div ref={useReveal<HTMLDivElement>()}>
        <div className={c.inner}><p className={`${c.eyebrow} anim-scroll`}>ORGANIZATION</p><h2 className={`${c.headline} anim-scroll`}>조직도</h2></div>
        <div className={`${c.orgWrap} anim-scroll`}><OrgChart /></div>
      </div>
    </section>
  </main>
);

// ── 인재상·채용 ──
export const CompanyCareers = observer(() => {
  const { talent } = useContent();
  const navigate = useNavigate();
  return (
    <main>
      <PageLoader />
      <SubPageHero title="인재상·채용" subtitle="함께 성장할 인재를 찾습니다" image={HERO} align="left" crumbs={crumbs('인재상·채용')} />
      <section className={c.section}>
        <div ref={useReveal<HTMLDivElement>()}>
          <div className={c.inner}>
            <p className={`${c.eyebrow} anim-scroll`}>{talent.eyebrow}</p>
            <h2 className={`${c.headline} anim-scroll`}>인재상</h2>
            <p className={`${c.lead} anim-scroll`}>{talent.headline}</p>
            <p className={`${c.talentIdeology} anim-scroll`}>{talent.ideology}</p>
          </div>
          <div className={`${c.talentWrap} anim-scroll`}><TalentValues /></div>
        </div>
      </section>
      <section className={`${c.section} ${c.alt}`}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>RECRUIT</p>
            <h2 className={c.headline}>채용 공고</h2>
            <p className={c.lead}>직무·근무지·채용공고와 지원 방법을 안내합니다.</p>
            <div className={x.soon} role="img" aria-label="채용 공고 준비중">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
              <span>채용 공고 · 준비중</span>
            </div>
            <div className={x.ctaRow}><Button variant="pill" onClick={() => navigate('/support/inquiry')}>채용 문의</Button></div>
          </Reveal>
        </div>
      </section>
    </main>
  );
});

// ── 연구개발 (신규) ──
export const CompanyRd = () => {
  const navigate = useNavigate();
  return (
    <main>
      <PageLoader />
      <SubPageHero title="연구개발" subtitle="기업부설연구소 중심의 지속적인 기술 개발" image={HERO} align="left" crumbs={crumbs('연구개발')} />
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>R&amp;D</p>
            <h2 className={c.headline}>기업부설연구소</h2>
            <p className={c.lead}>자체 기업부설연구소에서 하드웨어·펌웨어·응용 소프트웨어를 개발하고, 현장 경험을 제품 개선과 신사업으로 연결합니다.</p>
            <div className={`${x.cardGrid} ${x.three}`}>
              <div className={x.card}><Ic n="solution" /><h3>개발 범위</h3><p>IoT 게이트웨이·계측·관제 소프트웨어의 설계·개발·검증.</p></div>
              <div className={x.card}><Ic n="cycle" /><h3>제품 개선</h3><p>현장 피드백을 반영한 지속적인 제품 개선.</p></div>
              <div className={x.card}><Ic n="ai" /><h3>신사업</h3><p>안전(온열·한랭·가스)·환경 영역의 신규 솔루션 개발.</p></div>
            </div>
            <div className={x.soon} role="img" aria-label="연구소 인정서 준비중">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
              <span>연구소 인정서·개발 사진 · 준비중</span>
            </div>
            <div className={x.ctaRow}><a className={x.ghost} onClick={() => navigate('/company/certifications')}>인증·기술 페이지</a></div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

// ── 오시는 길 ──
export const CompanyLocation = observer(() => {
  const { location } = useContent();
  return (
    <main>
      <PageLoader />
      <SubPageHero title="오시는 길" subtitle="본사·서울 R&D 거점" image={HERO} align="left" crumbs={crumbs('오시는 길')} />
      <section className={c.section}>
        <div ref={useReveal<HTMLDivElement>()}>
          <div className={c.inner}><p className={`${c.eyebrow} anim-scroll`}>LOCATION</p><h2 className={`${c.headline} anim-scroll`}>오시는 길</h2></div>
          <div className={`${c.locGrid} anim-scroll`}>
            {location.offices.map((o) => {
              const src = 'mapEmbed' in o && o.mapEmbed
                ? o.mapEmbed
                : `https://maps.google.com/maps?q=${encodeURIComponent((o as { mapQuery: string }).mapQuery)}&z=16&hl=ko&output=embed`;
              return (
                <div className={c.office} key={o.label}>
                  <div className={c.map}><iframe src={src} title={`${o.label} 지도`} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" /></div>
                  <div className={c.officeInfo}>
                    <span className={c.officeLabel}>{o.label}</span>
                    <p className={c.officeAddr}>{o.address}</p>
                    <p className={c.officeTel}>Tel. {o.tel}</p>
                    {'fax' in o && <p className={c.officeTel}>Fax. {(o as { fax: string }).fax}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
});
